<template>
  <form @submit.prevent="handleUpdate" class="account-form">
    <section class="account-card">
      <header class="account-header">
        <div>
          <h1>Complete your account</h1>
          <p>Confirm your profile details and replace your temporary password.</p>
        </div>
        <img :src="brand.assets.logoMark" :alt="brand.productName" class="account-logo" />
      </header>

      <div class="account-profile-row">
        <div class="account-avatar">
          <LoadingSmallDiv v-if="isLoading" />
          <img v-else-if="user?.profile_img" :src="user.profile_img" alt="" />
          <img v-else-if="img" :src="img" alt="" />
          <span v-else>{{ userInitials }}</span>
        </div>
        <label class="account-upload">
          <span><Upload :size="16" /> Choose profile photo</span>
          <small>{{ inputFile || "JPG, PNG or GIF" }}</small>
          <input type="file" accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif" @change="handleFile" />
        </label>
        <p v-if="fileError" class="account-inline-error">{{ fileError }}</p>
        <p v-if="uploadError" class="account-inline-error">{{ uploadError }}</p>
      </div>

      <div class="account-section">
        <div class="account-section-heading">
          <h2>Profile details</h2>
          <p>These details identify you across the workspace.</p>
        </div>
        <div class="account-grid">
          <label class="account-field">
            <span>Full name</span>
            <input type="text" v-model="name" required />
          </label>
          <label class="account-field">
            <span>Slug</span>
            <input type="text" v-model="slug" readonly />
          </label>

          <template v-if="user.type === 'hmo' || user.type === 'provider' || user.type === 'client'">
            <label class="account-field">
              <span>Staff ID</span>
              <input type="text" v-model="staff_id" />
            </label>
            <label class="account-field">
              <span>Department</span>
              <input type="text" v-model="department" required />
            </label>
            <label class="account-field">
              <span>Designation</span>
              <input type="text" v-model="designation" required />
            </label>
            <label class="account-field">
              <span>Official line</span>
              <input type="text" v-model="official_line" />
            </label>
          </template>
        </div>
      </div>

      <div class="account-section">
        <div class="account-section-heading">
          <h2>Account security</h2>
          <p>Choose a secure password for future sign-ins.</p>
        </div>
        <div class="account-grid">
          <label class="account-field account-field-wide">
            <span>Email</span>
            <input type="email" v-model="email" readonly />
          </label>
          <label class="account-field">
            <span>New password</span>
            <input type="password" autocomplete="new-password" v-model="password" required />
          </label>
          <label class="account-field">
            <span>Confirm password</span>
            <input type="password" autocomplete="new-password" v-model="password_confirm" required />
          </label>
        </div>

        <transition name="account-notice">
          <div class="password-guidance" v-if="shouldShowPasswordNotice">
            <p>Password requirements</p>
            <ul>
              <li :class="{ met: passwordRules.hasLength }">
                <CheckCircle2 v-if="passwordRules.hasLength" :size="16" /><Circle v-else :size="16" />
                <span>8 or more characters</span>
              </li>
              <li :class="{ met: passwordRules.hasLetterOrSymbol }">
                <CheckCircle2 v-if="passwordRules.hasLetterOrSymbol" :size="16" /><Circle v-else :size="16" />
                <span>At least 1 letter or symbol</span>
              </li>
              <li :class="{ met: passwordRules.hasNumber }">
                <CheckCircle2 v-if="passwordRules.hasNumber" :size="16" /><Circle v-else :size="16" />
                <span>At least 1 number</span>
              </li>
              <li :class="{ met: passwordRules.isDifferentFromTemporary }" v-if="hasTemporaryPassword">
                <CheckCircle2 v-if="passwordRules.isDifferentFromTemporary" :size="16" /><Circle v-else :size="16" />
                <span>Different from the temporary password</span>
              </li>
              <li :class="{ met: passwordRules.confirmMatches }">
                <CheckCircle2 v-if="passwordRules.confirmMatches" :size="16" /><Circle v-else :size="16" />
                <span>Confirmation matches</span>
              </li>
            </ul>
          </div>
        </transition>
      </div>

      <footer class="account-actions">
        <span class="account-error" role="alert" v-if="error">{{ error }}</span>
        <button type="submit" class="account-submit" :disabled="isSubmitting || isLoading">
          <span class="account-spinner" v-if="isSubmitting" aria-hidden="true"></span>
          <CheckCircle2 v-else :size="17" />
          <span>{{ isSubmitting ? "Updating..." : "Complete setup" }}</span>
        </button>
      </footer>
    </section>
  </form>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from "vue";
import { CheckCircle2, Circle, Upload } from "@lucide/vue";
import putItem from "@/composables/putItem";
import useStorage from "@/composables/useStorage";
import LoadingSmallDiv from "@/components/LoadingSmallDiv.vue";
import brand from "@/whitelabel/brand.config";


export default {
    name: "UpdateUserComponent",
    components: { CheckCircle2, Circle, LoadingSmallDiv, Upload },
    props: ["user", "temporaryPassword"],
    emits: ["updated"],
    setup(props, context) {
        const file = ref(null);
        const inputFile = ref(null);
        const fileError = ref(null);
        const name = ref(null);
        const slug = ref(null);
        const designation = ref(null);
        const department = ref(null);
        const staff_id = ref(null);
        const official_line = ref(null);
        const password = ref(null);
        const password_confirm = ref(null);
        const email = ref(null);
        const isSubmitting = ref(false);
        const isLoading = ref(false);
        const error = ref(null);
        const img = ref(null);
        const types = ["image/jpg", "image/jpeg", "image/png", "image/gif"];
        const hasTemporaryPassword = computed(() => !!props.temporaryPassword);
        const userInitials = computed(() => String(props.user?.name || "User")
            .split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase());

        const passwordRules = computed(() => {
            const value = password.value || "";
            const confirm = password_confirm.value || "";

            return {
                hasLength: value.length >= 8,
                hasLetterOrSymbol: /[A-Za-z]|[^A-Za-z0-9]/.test(value),
                hasNumber: /\d/.test(value),
                isDifferentFromTemporary: !props.temporaryPassword || value !== props.temporaryPassword,
                confirmMatches: !!value && !!confirm && value === confirm,
            };
        });

        const shouldShowPasswordNotice = computed(() => !!password.value || !!password_confirm.value);

        const passwordPolicy = computed(() => {
            const rules = passwordRules.value;

            return {
                isValid:
                    rules.hasLength &&
                    rules.hasLetterOrSymbol &&
                    rules.hasNumber &&
                    rules.isDifferentFromTemporary &&
                    rules.confirmMatches,
            };
        });

        const getPasswordPolicyMessage = () => {
            const rules = passwordRules.value;

            if (!rules.hasLength || !rules.hasLetterOrSymbol || !rules.hasNumber) {
                return "Password must be at least 8 characters and include a number plus a letter or symbol.";
            }

            if (!rules.isDifferentFromTemporary) {
                return "Please choose a password that is different from the temporary password.";
            }

            if (!rules.confirmMatches) {
                return "Password and confirmation must match.";
            }

            return null;
        };

        // storage
        const { uploadFile, uploadProgress, uploadError, data, completed } =
            useStorage();

        // select
        onMounted(() => {
            // set other variables
            name.value = props.user.name;
            slug.value = props.user.slug;
            email.value = props.user.email;
            staff_id.value = props.user.staff_id;
            designation.value = props.user.designation;
            department.value = props.user.department;
            official_line.value = props.user.official_line;
        });

        // clear
        const clearForm = () => {
            file.value = null; //clear file value
            inputFile.value = null; // Clear the file input value
            isSubmitting.value = false;
            isLoading.value = false;
            uploadProgress.value = "0%";
        };

        // handle file
        const handleFile = (e) => {
            const selected = e.target.files[0];
            if (selected && types.includes(selected.type)) {
                file.value = selected;
                inputFile.value = file.value.name;
                fileError.value = null;
            } else {
                file.value = null;
                inputFile.value = null;
                fileError.value = "Please select an Image (.JPG, .PNG, .GIF)";
            }
        };

        //hand
        watchEffect(() => {
            if (file.value) {
                // start ploading
                isLoading.value = true;
                uploadError.value = null;

                // upload image
                uploadFile(file.value);
            }
        });

        //handle submit
        const handleUpdate = async () => {
            if (isSubmitting.value || isLoading.value) {
                return;
            }

            // clear error
            error.value = null;

            if (!passwordPolicy.value.isValid) {
                error.value = getPasswordPolicyMessage();
                return;
            }

            // cont
            if (password.value === password_confirm.value) {

                // start
                isSubmitting.value = true;
                error.value = false;
                uploadProgress.value = false;

                // variable
                const newVal = {
                    name: name.value,
                    profile_img: img.value ? img.value : props.user?.profile_img,
                    email: email.value,
                    slug: slug.value,
                    staff_id: staff_id.value,
                    designation: designation.value,
                    department: department.value,
                    official_line: official_line.value,
                    password: password.value,
                    status: "a", // make active
                };

                // post item
                const { res, err } = await putItem(
                    "authority/update",
                    props.user.id,
                    newVal,
                    null,
                    props.user?.token
                );

                if (res.value?.success && res.value?.data) {
                    // go back
                    context.emit("updated", res.value.data);
                } else {
                    error.value = err.value || res.value?.message ||
                        "We could not complete the account setup. Please try again.";
                }

                // end here
                // clear
                clearForm();

            } else {

                error.value = "Password and Confirmation doesn't match!"

            }

        };

        // watch tariff upload
        watchEffect(() => {
            if (completed.value) {
                // set image
                img.value = data.value;
                // clear
                clearForm();
                // data.succ.Success, data.succ.inserted_rows, data.succ.Error
                // console.log(data); // might not be necessary
            }
            if (uploadError.value) {
                // clear
                clearForm();
            }
        });


        return {
            brand,
            handleFile,
            handleUpdate,
            isSubmitting,
            isLoading,
            uploadProgress,
            fileError,
            uploadError,
            error,
            hasTemporaryPassword,
            userInitials,
            img,
            file,
            inputFile,
            name,
            slug,
            designation,
            department,
            staff_id,
            official_line,
            email,
            password,
            password_confirm,
            passwordRules,
            shouldShowPasswordNotice,
        };
    },
};
</script>

<style scoped>
.account-form { width: 100%; }
.account-card {
  background: var(--ui-card);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  box-shadow: var(--ui-shadow-xs);
  color: var(--ui-card-foreground);
  overflow: hidden;
}
.account-header {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  justify-content: space-between;
  padding: 1.5rem;
}
.account-header h1, .account-section-heading h2 {
  color: var(--ui-foreground);
  font-family: var(--ui-font-display);
  font-weight: 700;
  letter-spacing: -.02em;
  margin: 0;
}
.account-header h1 { font-size: 1.2rem; }
.account-header p, .account-section-heading p { color: var(--ui-muted-foreground); font-size: .82rem; margin: .35rem 0 0; }
.account-logo { height: 2.5rem; object-fit: contain; width: 2.5rem; }

.account-profile-row { align-items: center; display: flex; flex-wrap: wrap; gap: 1rem; padding: 1.5rem; }
.account-avatar {
  align-items: center;
  background: var(--ui-secondary);
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  color: var(--ui-secondary-foreground);
  display: flex;
  flex: 0 0 3.5rem;
  font-size: .85rem;
  font-weight: 700;
  height: 3.5rem;
  justify-content: center;
  overflow: hidden;
}
.account-avatar img { height: 100%; object-fit: cover; width: 100%; }
.account-upload {
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  cursor: pointer;
  display: grid;
  gap: .2rem;
  min-width: 14rem;
  padding: .65rem .8rem;
  position: relative;
}
.account-upload > span { align-items: center; color: var(--ui-foreground); display: flex; font-size: .78rem; font-weight: 600; gap: .45rem; }
.account-upload small { color: var(--ui-muted-foreground); font-size: .68rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-upload input { inset: 0; opacity: 0; position: absolute; width: 100%; }
.account-inline-error { color: var(--ui-destructive); font-size: .74rem; margin: 0; }

.account-section { border-top: 1px solid var(--ui-border); padding: 1.5rem; }
.account-section-heading { margin-bottom: 1.1rem; }
.account-section-heading h2 { font-size: .95rem; }
.account-grid { display: grid; gap: 1rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.account-field { display: grid; gap: .4rem; }
.account-field > span { color: var(--ui-foreground); font-size: .75rem; font-weight: 600; }
.account-field input {
  background: var(--ui-background);
  border: 1px solid var(--ui-input);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-foreground);
  font: inherit;
  font-size: .84rem;
  height: 2.65rem;
  margin: 0;
  padding: 0 .75rem;
  width: 100%;
}
.account-field input:focus { border-color: var(--ui-ring); box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 22%, transparent); outline: 0; }
.account-field input:read-only { background: var(--ui-muted); color: var(--ui-muted-foreground); }
.account-field-wide { grid-column: 1 / -1; }

.password-guidance { background: var(--ui-muted); border: 1px solid var(--ui-border); border-radius: calc(var(--ui-radius) - 2px); margin-top: 1rem; padding: .85rem; }
.password-guidance > p { color: var(--ui-foreground); font-size: .75rem; font-weight: 600; margin: 0 0 .6rem; }
.password-guidance ul { display: grid; gap: .4rem; grid-template-columns: repeat(2, minmax(0, 1fr)); list-style: none; margin: 0; padding: 0; }
.password-guidance li { align-items: center; color: var(--ui-muted-foreground); display: flex; font-size: .72rem; gap: .4rem; }
.password-guidance li.met { color: var(--ui-success); }

.account-actions { align-items: center; border-top: 1px solid var(--ui-border); display: flex; gap: 1rem; justify-content: flex-end; padding: 1rem 1.5rem; }
.account-error { color: var(--ui-destructive); font-size: .76rem; margin-right: auto; }
.account-submit {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  color: var(--ui-primary-foreground);
  display: flex;
  font: inherit;
  font-size: .8rem;
  font-weight: 600;
  gap: .5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 .9rem;
}
.account-submit:disabled { cursor: wait; opacity: .6; }
.account-spinner { animation: account-spin .7s linear infinite; border: 2px solid color-mix(in srgb, currentColor 35%, transparent); border-radius: 50%; border-top-color: currentColor; height: 1rem; width: 1rem; }
.account-notice-enter-active, .account-notice-leave-active { transition: opacity 150ms ease, transform 150ms ease; }
.account-notice-enter-from, .account-notice-leave-to { opacity: 0; transform: translateY(-.25rem); }
@keyframes account-spin { to { transform: rotate(360deg); } }

@media (max-width: 650px) {
  .account-header, .account-profile-row, .account-section, .account-actions { padding: 1.1rem; }
  .account-grid, .password-guidance ul { grid-template-columns: 1fr; }
  .account-field-wide { grid-column: auto; }
  .account-upload { flex: 1; min-width: 0; }
  .account-actions { align-items: stretch; flex-direction: column; }
  .account-error { margin: 0; }
  .account-submit { width: 100%; }
}
</style>
